import { useMemo } from 'react';
import { create } from 'zustand';

import type { Category } from '@domain/types';

import {
  createCategory,
  deleteCategory,
  getCategoryUsage,
  listCategories,
  updateCategory,
  type CategoryInput,
} from './repository';

type CategoriesState = {
  categories: Category[];
  loading: boolean;
  error: string | null;
  load: () => Promise<void>;
  create: (input: CategoryInput) => Promise<Category>;
  update: (id: string, patch: Partial<CategoryInput>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  usage: (id: string) => Promise<number>;
};

export const useCategoriesStore = create<CategoriesState>((set, get) => ({
  categories: [],
  loading: false,
  error: null,

  load: async () => {
    set({ loading: true, error: null });
    try {
      set({ categories: await listCategories(), loading: false });
    } catch (error) {
      set({ loading: false, error: (error as Error).message });
    }
  },

  create: async (input) => {
    const category = await createCategory(input);
    await get().load();
    return category;
  },

  update: async (id, patch) => {
    await updateCategory(id, patch);
    await get().load();
  },

  remove: async (id) => {
    await deleteCategory(id);
    await get().load();
  },

  usage: (id) => getCategoryUsage(id),
}));

export function useCategoryLookup(): Map<string, Category> {
  const categories = useCategoriesStore((state) => state.categories);
  return useMemo(
    () => new Map(categories.map((category) => [category.id, category])),
    [categories]
  );
}
